import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { resolveHelpIcon, helpTint } from './helpDisplay';
import { API_BASE_URL } from '../../api';

// Left-hand docs-style tree (categories + articles) for an article page — every
// category is listed, clicking one expands it in place to show its articles
// (fetched on demand for any category other than the current article's one,
// whose articles the parent already has), mirroring typical docs-site
// sidebars (e.g. kubernetes.io). Only clicking an article navigates.
const HelpSidebarNav = ({ categories, activeCategory, activeArticles, activePostSlug }) => {
	const { i18n } = useTranslation();
	const [expandedId, setExpandedId] = useState(activeCategory?.id ?? null);
	const [articlesByCategory, setArticlesByCategory] = useState({});
	const [loadingId, setLoadingId] = useState(null);

	useEffect(() => {
		setExpandedId(activeCategory?.id ?? null);
	}, [activeCategory?.id]);

	useEffect(() => {
		if (!activeCategory) return;
		setArticlesByCategory(prev => ({ ...prev, [activeCategory.id]: activeArticles }));
	}, [activeCategory, activeArticles]);

	const toggleCategory = (cat) => {
		if (expandedId === cat.id) {
			setExpandedId(null);
			return;
		}
		setExpandedId(cat.id);
		if (articlesByCategory[cat.id]) return;
		setLoadingId(cat.id);
		axios.get(`${API_BASE_URL}/api/v1/help/categories/${cat.slug}/articles`)
			.then(res => setArticlesByCategory(prev => ({ ...prev, [cat.id]: res.data })))
			.catch(() => setArticlesByCategory(prev => ({ ...prev, [cat.id]: [] })))
			.finally(() => setLoadingId(null));
	};

	return (
		<nav className="help-sidebar-nav">
			{categories.map(cat => {
				const isExpanded = cat.id === expandedId;
				const isActive = cat.id === activeCategory?.id;
				const articles = articlesByCategory[cat.id];
				return (
					<div key={cat.id} className="help-sidebar-nav__group">
						<button
							type="button"
							onClick={() => toggleCategory(cat)}
							className={`help-sidebar-nav__category${isActive ? ' help-sidebar-nav__category--active' : ''}`}
						>
							<FontAwesomeIcon
								icon={isExpanded ? faChevronDown : faChevronRight}
								className="help-sidebar-nav__caret"
							/>
							<FontAwesomeIcon
								icon={resolveHelpIcon(cat.icon)}
								className="help-sidebar-nav__icon"
								style={{ color: isActive ? helpTint(cat.color) : undefined }}
							/>
							<span>{cat.label}</span>
						</button>

						{isExpanded && (
							<div className="help-sidebar-nav__articles">
								{loadingId === cat.id ? (
									<span className="help-sidebar-nav__loading">Chargement...</span>
								) : (articles || []).map(article => (
									<NavLink
										key={article.id}
										to={`/${i18n.language}/help/${cat.slug}/${article.slug}`}
										className={`help-sidebar-nav__article${article.slug === activePostSlug ? ' help-sidebar-nav__article--active' : ''}`}
									>
										{article.title}
									</NavLink>
								))}
							</div>
						)}
					</div>
				);
			})}
		</nav>
	);
};

export default HelpSidebarNav;
